/**
 * Wire protocol: JSON text frames over the /ws WebSocket (DESIGN 5.3), plus
 * the hand-written guards the server uses to validate inbound frames.
 *
 * Field names are deliberately short: snapshots go out 10 times a second to
 * every client. Everything here is data only; the server never trusts a
 * client frame that did not pass `isClientMsg`.
 */

import type { BaseElement, Element } from './elements.js';
import { isBaseElement } from './elements.js';
import type { CastMode, FieldSig, SpellSpec } from './resolve.js';
import type { StatusId, DamageType } from './status.js';
import type { MagickId } from './magicks.js';
import type { GrandId, StashOutcome, StashSlot } from './weaves.js';
import type { EnemyKind } from './enemies.js';
import type { ModeId } from './modes.js';
import { isModeId } from './modes.js';
import type { Difficulty } from './difficulty.js';
import { isDifficulty } from './difficulty.js';
import {
  MAX_ACTS_PER_INPUT,
  MAX_INPUT_DT_SECONDS,
  MAX_CHAT_LENGTH,
  MAX_NAME_LENGTH,
  MAX_TOKEN_LENGTH,
  ROOM_CODE_LENGTH,
} from './constants.js';

export { PROTOCOL_VERSION } from './constants.js';

/** `story`: an act of story mode is being played (the story's `wave`). */
export type RoomPhase = 'lobby'|'countdown'|'wave'|'breather'|'gameover'|'story'|'match';
/** `room_crashed`: the room's tick threw 3 times in a row and was destroyed (DESIGN 5.7). */
export type ErrCode = 'bad_version'|'bad_msg'|'no_room'|'room_full'|'room_limit'|'not_host'|'not_ready'|'rate_limit'|'name_taken'|'room_crashed';

export interface MoveTarget { k:'t'; x:number; y:number }   // RMB click-to-move toward a world point
export interface MoveDir    { k:'d'; x:number; y:number }   // arrow keys, normalized
export interface MoveStop   { k:'s' }
export type MoveInput = MoveTarget | MoveDir | MoveStop;

/** `aim` is radians, y down (math.ts conventions), like every angle on the wire. */
export type Action =
  | { a:'el'; e:BaseElement }
  | { a:'clear' }
  // ph: begin / end (end only matters for force: releases beam/spray/charge). Additive: `h` on an end, the seconds the
  // client held the button, so a thrown rock flies the throw the client predicted (rockThrow.ts settleHold).
  | { a:'cast'; m:CastMode; ph:'b'|'e'; aim:number; h?:number }
  | { a:'magick' }
  | { a:'melee'; aim:number }
  /** Additive (docs/spells-depth.md): the weave key, and a tap on stash slot 0 or 1. */
  | { a:'weave' }
  | { a:'stash'; s:0|1 };

export type ClientMsg =
  | { t:'hello'; v:number; name:string; token?:string; color?:number }
  | { t:'create' }
  | { t:'join'; code:string }
  | { t:'ready'; ready:boolean }
  | { t:'start' }
  | { t:'input'; seq:number; mv:MoveInput; aim:number; acts:Action[]; /** seconds of movement this input covers (client prediction step); absent = legacy per-tick movement */ dt?:number;
      /** Additive (PvP): the server tick the client is drawing other players at (beams and arcs rewind to it), and its round trip in ms (the scoreboard). */
      vt?:number; rtt?:number }
  /** Additive (PvP lobby): pick a team. */
  | { t:'team'; team:0|1 }
  | { t:'ping'; c:number }
  | { t:'leave' }
  /** Not in DESIGN 5.3; added so the chat of DESIGN 6.1/6.6 has a frame. Max MAX_CHAT_LENGTH chars. */
  | { t:'chat'; text:string }
  /** The host picks the room's mode (lobby only). */
  | { t:'mode'; m:ModeId }
  /**
   * Additive (2026-10-28): "ready, go on" during a wait (the countdown, a breather, the results, a story wipe or the
   * Book's last words). Once every connected player has sent it the wait ends at once; alone, one press does it.
   */
  | { t:'next' }
  /** Additive (2026-10-28): open or close the menu. Alone in the room, the server freezes the match while it is open. */
  | { t:'pause'; on:boolean }
  /** Additive (2026-10-28): the host picks the difficulty (lobby only; difficulty.ts). */
  | { t:'difficulty'; d:Difficulty };

/** A wait the players can cut short with `next` (Room `wait`, World.waitKind). */
export type WaitKind = 'countdown'|'breather'|'results'|'wipe'|'end';

/** `team`: the player's team in a team mode (absent otherwise). */
export interface LobbyPlayer { id:number; name:string; color:number; ready:boolean; connected:boolean; host:boolean; team?:0|1 }
/** `mode`: what the room plays (modes.ts); absent from older servers, which means waves. */
/** `difficulty`: the host's choice in the lobby (difficulty.ts; Normal from older servers). */
export interface RoomSettings { difficulty:Difficulty; maxPlayers:4; mode?:ModeId }

// Snapshot conventions: angles (`aim`, `ang`, `f`, `half`) are radians, y down
// (math.ts); `t` fields are seconds left; `el` is the spell's sorted element list.
/** `s` = stacks (burning 1..6, otherwise 1). */
export interface StatusSnap { id:StatusId; t:number; s:number }
export interface PlayerSnap {
  id:number; x:number; y:number; vx:number; vy:number; aim:number; hp:number; dead:0|1;
  q:Element[]; st:StatusSnap[]; ch:number /* charge 0..1 */; act:'idle'|'beam'|'spray'|'arc'|'charge'|'melee'|'cast';
  bub:number /* bubble hp, 0 none */; arm:'none'|'earth'|'ice'; con:0|1; sm:number /* speed multiplier */;
  /** Elements imbued on the staff and swings left, when imbued. */
  imb?:Element[]; imc?:number;
  /** Additive: the elements of the rock (or shards) held while `act` is 'charge', so everyone sees what is coming. */
  che?:Element[];
  /**
   * Additive (PvP): `tm` team; `rs` seconds to respawn while dead; `sh` while the spawn shield holds; `mc` seconds of
   * Magick cooldown left; `pg` round trip in ms; `fl` the team whose flag this wizard carries; `kd` kills and deaths.
   */
  tm?:0|1; rs?:number; sh?:1; mc?:number; pg?:number; fl?:0|1; kd?:[number, number];
  /**
   * Additive (docs/spells-depth.md): `sx` the two stash slots (while either holds a piece); `wv` the piece being woven and
   * how far along (0..1); `gi` the Grand Magick being incanted and how far along; `gc` seconds of Grand Magick cooldown.
   * Slice 2: `lc` seconds of the Lesser Time Stop's own cooldown (co-op); `wd` while a Second Wind or Soul Ward waits
   * for the killing blow; `dm` seconds until a Death mark on this wizard falls; `ts` how fast its time runs while that
   * isn't 1 (Time Stop, a slowing field, Quickening): below 0.5 it can't start a cast, a Magick or a weave.
   */
  sx?:StashSlot[]; wv?:[Element, number]; gi?:[GrandId, number]; gc?:number; lc?:number; wd?:1; dm?:number; ts?:number }
export interface EnemySnap { id:number; k:EnemyKind; x:number; y:number; vx:number; vy:number; hp:number; mhp:number; ar:number; st:StatusSnap[]; f:number /* facing */; a:'idle'|'chase'|'attack'|'panic'|'channel';
  /** Additive: a boss in its second phase, or Mother Mire in her second or third (absent in phase 1 and for everything else). */
  ph?:2|3;
  /** Additive (story mode): Mother Mire sunk in her pool (out of reach), or stranded on its ice. */
  sub?:1; str?:1;
  /** Additive (difficulty.ts): an elite, and the element it is immune to. */
  el?:Element;
  /**
   * Additive (story mode): a level object riding on the dummy body (static,
   * never counted, takes no damage): `brazier`, and `lit` while it burns;
   * `boiler`, and `os` its [water, heat, pressure], each 0..1.
   */
  ob?:'brazier'|'boiler'; lit?:1; os?:number[];
  /** Additive (story mode): the level object's id (the Everflame is a brazier with its own look). */
  oi?:string;
  /** Additive (docs/spells-depth.md slice 2): seconds until a Death mark or Doom on it falls. */
  dm?:number }
/**
 * Additive (story mode): the map's changing parts, cells as y * w + x. `d`:
 * ids of the open doors; `i`: deep water frozen into ice. Brambles: `a` burnt
 * to ash, `b` burning, `wt` wet. The chasm: `m` molten rock, `s` set to
 * stone. The bramble and chasm lists are absent while empty. Client
 * prediction rebuilds the same walls from these (level.ts withTerrain).
 */
export interface TerrainSnap { d:string[]; i:number[]; a?:number[]; b?:number[]; wt?:number[]; m?:number[]; s?:number[];
  /** Rooms whose floor is flooded right now (Mother Mire), and 1 while the flood boils. */
  f?:string[]; bo?:1 }
/**
 * Additive (story mode): where the party is in the act. `a` act title,
 * `ti` the room's title, `o` the objective line, `c` the claimed rune stone
 * ('' none yet), `t` seconds left of a wipe or the ending.
 */
export interface StorySnap { a:string; ti:string; o:string; c:string; t:number }
/**
 * Additive (2026-10-27): things on the field that are neither bodies nor
 * spells. The Magicks Meteor Shower (one marker per falling rock), Tornado,
 * Thunder Storm, Vortex and Summon Elemental, and the jotunn's tells
 * (`fissure`: a line of length `r` along `ang`; `frost`: a ring growing to
 * `r`). `t` is seconds left: to the impact or the strike for a meteor or a
 * tell, of life otherwise. `el` is what a tornado or elemental was fed.
 * `act` is seconds since the elemental last slammed. `tt` (additive) is a boss
 * tell's full length in s, which difficulty and the rage change.
 */
export type MarkerKind = 'meteor'|'tornado'|'storm'|'vortex'|'elemental'|'fissure'|'frost';
export interface MarkerSnap { id:number; o:number; k:MarkerKind; x:number; y:number; r:number; ang:number; t:number; el:Element[]; act?:number; tt?:number }
/** Additive: `z` a thrown rock's height above the ground (rockThrow.ts rockHeight). */
export interface ProjSnap { id:number; o:number /* owner */; k:'boulder'|'shard'|'arrow'|'bomb'; x:number; y:number; vx:number; vy:number; el:Element[]; r:number; fuse?:number; z?:number }
/**
 * A beam's leg from the staff to where it stops (`len`). `merged` 1 while it
 * crosses another beam and bounces; additive `bo` is then the bounce
 * (beamCross.ts): it leaves the crossing (x, y) at `ang` for `len`, hitting
 * `hit` (-1 none), its damage x `mul`.
 */
export interface BeamBounce { x:number; y:number; ang:number; len:number; hit:number; mul:number }
export interface BeamSnap { id:number; o:number; x:number; y:number; ang:number; len:number; el:Element[]; hit:number /* entity id or -1 */; merged:0|1; bo?:BeamBounce }
export interface SpraySnap { id:number; o:number; x:number; y:number; ang:number; range:number; half:number; el:Element[] }
export interface ArcSnap { id:number; o:number; pts:[number,number][]; el:Element[] }
/** `sg` (additive): a field's signature, so the client draws a wave, an orb, a whirlpool or a star as itself. */
export interface HazardSnap { id:number; o:number; k:'barrier'|'wall'|'mine'|'storm'|'aura'|'pool'|'field'; x:number; y:number; ang:number; geo:'arc'|'ring'|'line'|'point'; el:Element[]; hp:number; t:number; r:number; sg?:FieldSig }
/**
 * `ts` / `tsb` (additive): seconds of Time Stop left and who cast it, while one runs; `tsl` 1 when it is the Lesser
 * Time Stop (only the caster's foes slow). Slice 2's room-wide Grand Magicks, while they run: `qk` seconds of
 * Quickening (`qkb` its caster), `fl` of the Great Flood, `ew` of Eternal Winter.
 */
export interface WaveSnap { n:number; alive:number; total:number; phase:RoomPhase; t:number /* seconds left in countdown/breather */; rain:number; blizzard:number; timewarp:number; ts?:number; tsb?:number;
  tsl?:1; qk?:number; qkb?:number; fl?:number; ew?:number }

export type GameEvent =
  | { e:'cast'; p:number; kind:SpellSpec['kind']; el:Element[]; x:number; y:number; ang:number; m:CastMode }
  | { e:'hit'; tgt:number; dmg:number; type:DamageType; x:number; y:number; crit:0|1 /* frozen x3 or wet x2 */ }
  | { e:'heal'; tgt:number; amt:number }
  | { e:'status'; tgt:number; id:StatusId; on:0|1 }
  | { e:'death'; tgt:number; by:number; ff:0|1 }
  | { e:'revive'; tgt:number; by:number }
  | { e:'explosion'; x:number; y:number; r:number; el:Element[]; kind:'blast'|'beamcross'|'bomb'|'mine'|'meteor'|'vortex'|'slam'|'frost' }
  /** Additive: a boss enrages (phase 2), or a tell starts (`tell`). */
  | { e:'boss'; id:number; act:'rage'|'tell'; x:number; y:number }
  | { e:'beam_merge'; a:number; b:number; x:number; y:number }
  | { e:'shock_self'; p:number }
  | { e:'magick'; p:number; id:MagickId; x:number; y:number }
  /** Additive (docs/spells-depth.md): a weave starts (`el` its result, `s` its seconds), won't hold, or breaks; a finished weave; a stash tap; a Grand Magick's incantation, cast, refusal, break and end. */
  | { e:'weave'; p:number; o:'start'|'fail'|'break'; el?:Element; s?:number }
  | { e:'woven'; p:number; el:Element; tier:number; x:number; y:number }
  | { e:'stash'; p:number; s:number; o:StashOutcome; el?:Element }
  | { e:'grand'; p:number; id:GrandId; o:'start'|'cast'|'wait'|'break'|'end'; x:number; y:number }
  /** Additive (slice 2): a Second Wind or Soul Ward turns a killing blow; a Death mark or Doom falls on `tgt`. */
  | { e:'ward'; tgt:number; x:number; y:number }
  | { e:'doom'; tgt:number; x:number; y:number }
  | { e:'imbue'; p:number; el:Element[] }
  | { e:'queue_full'; p:number } | { e:'cancel'; p:number; el:Element }
  | { e:'wave'; n:number; phase:RoomPhase }
  | { e:'bolt'; x:number; y:number; tgt:number }
  | { e:'ff'; by:number; tgt:number; dmg:number }
  /** Story mode: the Book says a line. */
  | { e:'say'; text:string }
  /** Story mode: a door opens or shuts (`art`: the door's prop, the rune door's has its own sound), a brazier catches or goes out, a rune stone is claimed. */
  | { e:'door'; id:string; open:0|1; x:number; y:number; art?:string }
  | { e:'lit'; tgt:number; on:0|1 }
  | { e:'checkpoint'; id:string; x:number; y:number }
  /** Story mode: lightning ran through the water from `from` to `tgt`. */
  | { e:'conduct'; from:number; tgt:number }
  /** Story mode: a room's enemies climb out of a crack (or a gate) here. */
  | { e:'emerge'; x:number; y:number }
  /** Story mode: deep water froze into a bridge, the bridge is about to give, or it melted; (x, y) is its middle. */
  | { e:'ice'; act:'form'|'crack'|'melt'; x:number; y:number }
  /** Story mode: brambles caught fire (spreading) or burnt away to ash; (x, y) is the middle of the cells. */
  | { e:'bramble'; act:'burn'|'ash'; x:number; y:number }
  /** Story mode: lava filled chasm cells, cold set it to stone, or it sank away unset. */
  | { e:'lava'; act:'fill'|'set'|'sink'; x:number; y:number }
  /** Story mode: a body was knocked into the chasm (it dies). */
  | { e:'fall'; tgt:number; x:number; y:number }
  /** Story mode: water went into the boiler, it is full, or its gauge stepped up (`v` the step, 1..BOILER.gaugeSteps). */
  | { e:'boiler'; act:'fill'|'full'|'gauge'; tgt:number; x:number; y:number; v?:number }
  /**
   * Story mode: Mother Mire. `flood` / `drain` the chapel floods and drains; she `sink`s into her pool, `rise`s out
   * of it or is `strand`ed on its ice; `call` her spiders; `phase` her next phase begins (`v` 2 or 3); the
   * `everflame` wakes (`v` 0), is lit for the boil (`v` 1) or relit at her death (`v` 2).
   */
  /** Capture the flag: `team`'s flag was taken, dropped, sent home or captured by `by` (-1 nobody: it returned by itself). */
  | { e:'flag'; act:'take'|'drop'|'return'|'capture'; team:0|1; by:number; x:number; y:number }
  | { e:'mire'; act:'flood'|'drain'|'sink'|'rise'|'strand'|'call'|'phase'|'everflame'; id:number; x:number; y:number; v?:number };

/** `mode` and `won` are story additions: an act reached its end (`won` 1) or the party gave up. */
/** `teams` and `winner` are capture-the-flag additions: the captures per team and who won (-1 a draw); `team` per player. */
export interface MatchResult { wave:number; score:number; players:{ id:number; name:string; kills:number; dmg:number; heal:number; ff:number; deaths:number; revives:number; team?:0|1 }[]; menace:number; mode?:ModeId; won?:0|1; teams?:[number, number]; winner?:0|1|-1;
  /** Additive (2026-10-28): the difficulty it was played on (waves and story; absent in capture the flag). */
  difficulty?:Difficulty }
/**
 * Additive (capture the flag): `s` captures per team; `f` each team's flag: `st` home, carried (`by` the carrier)
 * or dropped (`t` seconds before it goes home); `t` seconds of match left; `cap` captures to win.
 */
export interface FlagSnap { st:'home'|'carried'|'dropped'; x:number; y:number; by?:number; t?:number }
export interface CtfSnap { s:[number, number]; f:[FlagSnap, FlagSnap]; t:number; cap:number }

export type ServerMsg =
  | { t:'welcome'; pid:number; token:string; v:number; tickRate:number; snapEvery:number }
  /**
   * `level`: the map the room plays (levels/index.ts id); absent from older servers, which means the arena.
   * Additive (2026-10-28): `wait` the wait running now, which `next` skips (players' `ready` = who has pressed it);
   * `paused` 1 while the room's only player has the menu open and the match is frozen.
   */
  | { t:'room'; code:string; phase:RoomPhase; host:number; players:LobbyPlayer[]; settings:RoomSettings; level?:string; wait?:WaitKind; paused?:1 }
  | { t:'snap'; tick:number; st:number /* server ms */; ack:number /* last input seq applied for this client */;
      p:PlayerSnap[]; e:EnemySnap[]; pr:ProjSnap[]; b:BeamSnap[]; s:SpraySnap[]; ar:ArcSnap[]; h:HazardSnap[]; w:WaveSnap; ev:GameEvent[];
      /** Additive: field markers (Magicks, boss tells); absent when there are none. */
      mk?:MarkerSnap[];
      /** Additive (story mode): open doors and frozen water; absent on maps with neither. */
      tr?:TerrainSnap;
      /** Additive (story mode): act, room, objective, checkpoint. */
      sy?:StorySnap;
      /** Additive (capture the flag): scores, flags and the clock. */
      ctf?:CtfSnap }
  | { t:'pong'; c:number; st:number }
  | { t:'end'; result:MatchResult }
  | { t:'err'; code:ErrCode; msg:string }
  /** Not in DESIGN 5.3; the server relays a player's chat line to the room. `st` = server ms. */
  | { t:'chat'; from:number; name:string; text:string; st:number };

/** Convenience aliases for the two frames most code pattern-matches on. */
export type SnapMsg = Extract<ServerMsg, { t:'snap' }>;
export type InputMsg = Extract<ClientMsg, { t:'input' }>;

// ------------------------------------------------------------------ validation (DESIGN 5.3 "Validation", 5.7)

/** Room codes: exactly ROOM_CODE_LENGTH uppercase ASCII letters. */
export const ROOM_CODE_RE = new RegExp(`^[A-Z]{${ROOM_CODE_LENGTH}}$`);

const CAST_MODE_SET: ReadonlySet<string> = new Set<CastMode>(['force', 'area', 'self', 'weapon']);

/** Longest hold a release may claim (`h`): far past a full charge, so only nonsense is refused. */
const MAX_HOLD_SECONDS = 600;

type Dict = Record<string, unknown>;

/**
 * Own keys that would let a frame reach a prototype through Object.assign or
 * a deep merge. JSON.parse turns them into plain own properties, so a frame
 * carrying one is rejected outright instead of "ignored as an extra field".
 */
const FORBIDDEN_KEYS = ['__proto__', 'constructor', 'prototype'] as const;

/**
 * MoveDir length tolerance: generous enough for a client that rounds its
 * normalised vector to a few decimals (inputs go 20/s), far too small to be
 * a speed hack (sqrt(1.001) = +0.05%).
 */
const DIR_SLACK = 1e-3;

/** A plain object (not null, not an array) with no prototype-pollution keys. */
function isDict(x: unknown): x is Dict {
  if (typeof x !== 'object' || x === null || Array.isArray(x)) return false;
  for (const k of FORBIDDEN_KEYS) if (Object.hasOwn(x, k)) return false;
  return true;
}
function isNum(x: unknown): x is number {
  return typeof x === 'number' && Number.isFinite(x);
}
/** Safe integers only: 2^53 + 2 is an "integer" but cannot be counted up from. */
function isInt(x: unknown): x is number {
  return typeof x === 'number' && Number.isSafeInteger(x);
}
function isStr(x: unknown, max: number): x is string {
  return typeof x === 'string' && x.length <= max;
}

export function isCastMode(x: unknown): x is CastMode {
  return typeof x === 'string' && CAST_MODE_SET.has(x);
}

export function isRoomCode(x: unknown): x is string {
  return typeof x === 'string' && ROOM_CODE_RE.test(x);
}

/**
 * MoveDir must be normalised by the client; a direction longer than 1 (plus
 * DIR_SLACK) is a speed hack and is rejected here rather than silently
 * clamped.
 */
export function isMoveInput(x: unknown): x is MoveInput {
  if (!isDict(x)) return false;
  switch (x['k']) {
    case 's': return true;
    case 't': return isNum(x['x']) && isNum(x['y']);
    case 'd': {
      const dx = x['x'], dy = x['y'];
      return isNum(dx) && isNum(dy) && dx * dx + dy * dy <= 1 + DIR_SLACK;
    }
    default: return false;
  }
}

export function isAction(x: unknown): x is Action {
  if (!isDict(x)) return false;
  switch (x['a']) {
    case 'el': return isBaseElement(x['e']);
    case 'clear': return true;
    case 'cast': return isCastMode(x['m']) && (x['ph'] === 'b' || x['ph'] === 'e') && isNum(x['aim'])
      && (x['h'] === undefined || (isNum(x['h']) && (x['h'] as number) >= 0 && (x['h'] as number) <= MAX_HOLD_SECONDS));
    case 'magick': return true;
    case 'melee': return isNum(x['aim']);
    case 'weave': return true;
    case 'stash': return x['s'] === 0 || x['s'] === 1;
    default: return false;
  }
}

/**
 * Shape check for one inbound frame. Rejects: unknown or missing `t`, arrays
 * where an object is required, any non-finite number, non-safe or negative
 * integers where an integer is required (seq, v, color), non-base elements,
 * more than MAX_ACTS_PER_INPUT actions, names over MAX_NAME_LENGTH, codes
 * that are not 5 uppercase letters, chat over MAX_CHAT_LENGTH, and any
 * object (at any depth) carrying a prototype-pollution key. Other extra
 * fields are ignored. An empty name is accepted: the server assigns a fun
 * one (DESIGN 6.6).
 */
export function isClientMsg(x: unknown): x is ClientMsg {
  if (!isDict(x)) return false;
  switch (x['t']) {
    case 'hello':
      return isInt(x['v'])
        && isStr(x['name'], MAX_NAME_LENGTH)
        && (x['token'] === undefined || isStr(x['token'], MAX_TOKEN_LENGTH))
        && (x['color'] === undefined || isInt(x['color']));
    case 'create': return true;
    case 'join': return isRoomCode(x['code']);
    case 'ready': return typeof x['ready'] === 'boolean';
    case 'start': return true;
    case 'mode': return isModeId(x['m']);
    case 'team': return x['team'] === 0 || x['team'] === 1;
    case 'input': {
      const acts = x['acts'];
      return isInt(x['seq']) && (x['seq'] as number) >= 0
        && isMoveInput(x['mv'])
        && isNum(x['aim'])
        && (x['dt'] === undefined || (isNum(x['dt']) && (x['dt'] as number) >= 0 && (x['dt'] as number) <= MAX_INPUT_DT_SECONDS))
        && Array.isArray(acts) && acts.length <= MAX_ACTS_PER_INPUT && acts.every(isAction)
        && (x['vt'] === undefined || (isNum(x['vt']) && (x['vt'] as number) >= 0))
        && (x['rtt'] === undefined || (isNum(x['rtt']) && (x['rtt'] as number) >= 0 && (x['rtt'] as number) <= 60_000));
    }
    case 'ping': return isNum(x['c']);
    case 'leave': return true;
    case 'chat': return isStr(x['text'], MAX_CHAT_LENGTH);
    case 'next': return true;
    case 'pause': return typeof x['on'] === 'boolean';
    case 'difficulty': return isDifficulty(x['d']);
    default: return false;
  }
}

/**
 * Parse one text frame: JSON then `isClientMsg`. Returns null for anything
 * that is not a well-formed ClientMsg (including JSON parse errors).
 */
export function parseClientMsg(text: string): ClientMsg | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return null;
  }
  return isClientMsg(parsed) ? parsed : null;
}
